<script>
import { ref } from 'vue'
import GraphCpuLoadLines from '@/components/graphs/Graph.Cpu.Load.Lines.vue'
import GraphDiskUsageHistory from '@/components/graphs/Graph.Disk.Usage.History.vue'
import GraphNetworksBw from '@/components/graphs/Graph.Networks.Bw.vue'
import GraphNetworksPk from '@/components/graphs/Graph.Networks.Pk.vue'
import GraphNetworksPE from '@/components/graphs/Graph.Networks.PE.vue'
import MemoryUtilization from '@/components/graphs/Memory.Utilization.vue'
import CpuUtilization from '@/components/graphs/Graph.Cpu.Utilization.vue'
import GraphLicense from '@/components/graphs/Graph.License.Graphs.vue'
import GraphDeviceRoleHistory from '@/components/graphs/Graph.Device.Role.History.vue'
import api from '@/api'
import { calculateAggregationInterval } from '@/data/chartDrillDown'
import { graphMethods } from '@/state/helpers'

export default {
  props: {
    deviceId: {
      type: String,
      required: true,
    },
    componentname: {
      type: String,
      required: true,
    },
    iface: {
      type: String,
      default: 'wan1',
      required: false,
    },
  },
  page: {
    title: 'Analytics Dashboard',
    meta: [
      {
        name: 'description',
      },
    ],
  },
  components: {
    GraphCpuLoadLines,
    GraphDiskUsageHistory,
    GraphNetworksBw,
    GraphNetworksPk,
    GraphNetworksPE,
    MemoryUtilization,
    CpuUtilization,
    GraphLicense,
    GraphDeviceRoleHistory,
  },
  data() {
    return {
      pagetitle: 'Analytics Dashboard',
      device: null,
      interfaces: [],
      currentComponent: null,
      activeTab: 'today',
      selectedMonth: '',
    }
  },
  setup() {
    const refreshInterval = ref(0)

    return {
      refreshInterval,
    }
  },
  methods: {
    ...graphMethods,
    loadGraph(dur = 'today') {
      var timeStart = '-1d'
      var timeStop = '0m'
      var interval = '60m'
      var now = new Date()
      var diff = 0
      var _timeStart = 0
      this.selectedMonth = ''
      switch (dur) {
        case 'today': {
          // Today means since local midnight, not a rolling 24 hours.
          const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
          const minutesSinceMidnight = Math.max(1, Math.floor((now.getTime() - midnight.getTime()) / (1000 * 60)))
          timeStart = `-${minutesSinceMidnight}m`
          timeStop = '0m'
          interval = calculateAggregationInterval(midnight.getTime(), now.getTime())
          break
        }
        case 'yesterday':
          var yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
          diff = now.getTime() - yesterday.getTime()
          _timeStart = -Math.floor(diff / (1000 * 60))
          var _timeStop = _timeStart + 1440
          timeStart = `${_timeStart}m`
          timeStop = `${_timeStop}m`
          break
        case 'this-week':
          var lastWeek = new Date(now.getTime() - 604800000)
          diff = now.getTime() - lastWeek.getTime()
          _timeStart = -Math.floor(diff / (1000 * 60))
          timeStart = `${_timeStart}m`
          timeStop = '0m'
          interval = '1d'
          break
        case 'last-3months': {
          timeStart = `-90d`
          timeStop = `0d`
          interval = '1d'
          break
        }
        case 'last-year': {
          timeStart = `-365d`
          timeStop = `0d`
          interval = '1w'
          break
        }
        default:
          if (dur.startsWith('month-')) {
            const month = parseInt(dur.split('-')[1])
            const now = new Date()
            const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth() - (month - 1), 1)
            const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() - (month - 1) + 1, 0)
            const startDays = Math.floor((now.getTime() - firstDayOfMonth.getTime()) / (1000 * 60 * 60 * 24))
            const stopDays = Math.floor((now.getTime() - lastDayOfMonth.getTime()) / (1000 * 60 * 60 * 24))

            timeStart = startDays === 0 ? 'today()' : `-${startDays}d`
            timeStop = stopDays === 0 ? 'today()' : `-${stopDays}d`
            interval = '1d'
            this.selectedMonth = dur
          }

          if (dur == 'month-1') {
            timeStop = '0m'
          }
          break
      }

      this.activeTab = dur

      this.changeGraphDurationStart(timeStart)
      this.changeGraphDurationEnd(timeStop)
      this.changeGraphInterval(interval)
      this.incrementGraphUpdate()
    },
  },
  mounted() {
    if (this.componentname == 'cpu-utilization') {
      this.currentComponent = CpuUtilization
    } else if (this.componentname == 'cpu-load') {
      this.currentComponent = GraphCpuLoadLines
    } else if (this.componentname == 'disk-utilization') {
      this.currentComponent = GraphDiskUsageHistory
    } else if (this.componentname == 'memory-utilization') {
      this.currentComponent = MemoryUtilization
    } else if (this.componentname == 'networks-bw') {
      this.currentComponent = GraphNetworksBw
    } else if (this.componentname == 'networks-pk') {
      this.currentComponent = GraphNetworksPk
    } else if (this.componentname == 'networks-pe') {
      this.currentComponent = GraphNetworksPE
    } else if (this.componentname == 'license') {
      this.currentComponent = GraphLicense
    } else if (this.componentname == 'device-role') {
      // Role history is Ezemanage-login only
      if (this.authUserIsEzemanage) {
        this.currentComponent = GraphDeviceRoleHistory
      } else {
        this.$router.replace({ name: 'analytics-dashboard', params: { deviceId: this.deviceId } })
        return
      }
    }

    api.get(`/devices/${this.deviceId}`).then(async (response) => {
      this.device = response.data
      // CPU count must come from the device type (same source as the main dashboard).
      if (this.device?.deviceTypeId) {
        try {
          const typeRes = await api.get(`/device-types/${this.device.deviceTypeId}`)
          const deviceType = typeRes.data.deviceType
          if (deviceType?.cpuCount != null) {
            this.device = { ...this.device, cpuCount: deviceType.cpuCount }
          }
        } catch (e) {
          console.error('Failed to load device type CPU count', e)
        }
      }

      // Charts start from their own local defaults, so apply the Today range
      // once they are mounted to keep the initial view in sync with the tab.
      await this.$nextTick()
      this.loadGraph('today')
    })
  },
}
</script>

<template>
  <Teleport to="#elePageTitle">
    {{ pagetitle + ' - ' + (device ? device.host : '') }}
  </Teleport>

  <div v-if="device" class="row mb-3">
    <div class="col-md-12 d-flex align-items-center">
      <!-- Tabs -->
      <ul class="nav nav-tabs nav-tabs-custom flex-grow-0" role="tablist">
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'today' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('today')">Today</a>
        </li>
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'yesterday' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('yesterday')">Yesterday</a>
        </li>
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'this-week' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('this-week')">Last 7 Days</a>
        </li>
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'month-1' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('month-1')">This Month</a>
        </li>
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'month-2' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('month-2')">Last Month</a>
        </li>
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'last-3months' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('last-3months')">Last 3 Months</a>
        </li>
        <li class="nav-item">
          <a :class="['nav-link', { active: activeTab === 'last-year' }]" data-bs-toggle="tab" href="#" role="tab" aria-selected="true" @click="loadGraph('last-year')">Last 1 Year</a>
        </li>
      </ul>

      <!-- Month Select -->
      <select v-model="selectedMonth" @change="loadGraph(selectedMonth)" class="form-select ms-auto" style="width: 200px">
        <option disabled value="">Select Month</option>
        <option v-for="n in 12" :key="n" :value="'month-' + n">
          {{ new Date(new Date().getFullYear(), new Date().getMonth() - (n - 1), 1).toLocaleString('default', { month: 'long', year: 'numeric' }) }}
        </option>
      </select>
    </div>
  </div>

  <div v-if="device" class="row">
    <div class="col-md-12">
      <component
        :is="currentComponent"
        :host="device.host"
        :refresh="refreshInterval"
        :type="device.protocolType"
        :iface="iface"
        :cpuCount="device.cpuCount"
      />
      <GraphNetworksPk v-if="this.componentname == 'networks-bw'" :title="`Network (Packet) `" :host="device.host" :refresh="refreshInterval" :type="device.protocolType" :iface="iface" />
      <GraphNetworksPE v-if="this.componentname == 'networks-bw'" :title="`Network (Packet - Error) `" :host="device.host" :refresh="refreshInterval" :type="device.protocolType" :iface="iface" />
    </div>
  </div>
</template>